import React, { useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  PlusOutlined,
  SnippetsOutlined,
} from '@ant-design/icons'
import { Button, Input, Modal, Select, Space, Switch, Tooltip, Typography } from 'antd'
import {
  PortalArticleTableColumn,
  PortalArticleTableItem,
} from '../../../../models/PortalArtigoModel'
import {
  PORTAL_TABLE_COLUMN_ALIGN_OPTIONS,
  PORTAL_TABLE_COLUMN_STYLE_OPTIONS,
  PORTAL_TABLE_COLUMN_WIDTH_OPTIONS,
} from '../../helpers/portal-editor-options'
import {
  createPortalTableColumn,
  createPortalTableRow,
  hasPortalTableGridContent,
  PORTAL_TABLE_MAX_COLUMNS,
  PORTAL_TABLE_MAX_ROWS,
} from '../../helpers/portal-table'
import { PortalTableClipboardGrid } from '../../helpers/portal-table-clipboard'
import { createPortalEditorBlockId } from '../../helpers/block-defaults'
import { portalEditorColors } from '../PortalEditorTheme'
import { PortalTablePasteModal } from './PortalTablePasteModal'

interface Props {
  open: boolean
  value: PortalArticleTableItem
  onCancel: () => void
  onApply: (table: PortalArticleTableItem) => void
}

export function PortalTableEditorModal({ open, value, onCancel, onApply }: Props) {
  const [draft, setDraft] = useState<PortalArticleTableItem>(() => cloneTable(value))
  const [initialSnapshot, setInitialSnapshot] = useState('')
  const [pasteOpen, setPasteOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const next = cloneTable(value)
    setDraft(next)
    setInitialSnapshot(JSON.stringify(next))
    setPasteOpen(false)
  }, [open, value])

  const dirty = useMemo(() => JSON.stringify(draft) !== initialSnapshot, [draft, initialSnapshot])
  const exportEnabled = draft.export?.enabled !== false

  const requestClose = () => {
    if (!dirty) {
      onCancel()
      return
    }
    Modal.confirm({
      title: 'Descartar alterações da tabela?',
      content: 'As mudanças feitas desde que o modal foi aberto serão perdidas.',
      okText: 'Descartar',
      cancelText: 'Continuar editando',
      okType: 'danger',
      onOk: onCancel,
    })
  }

  const updateColumn = (columnIndex: number, patch: Partial<PortalArticleTableColumn>) => {
    setDraft((current) => ({
      ...current,
      columns: current.columns.map((column, index) =>
        index === columnIndex ? { ...column, ...patch } : column
      ),
    }))
  }

  const moveColumn = (columnIndex: number, direction: -1 | 1) => {
    setDraft((current) => ({
      ...current,
      columns: moveArrayItem(current.columns, columnIndex, columnIndex + direction),
    }))
  }

  const addColumn = () => {
    setDraft((current) => {
      if (current.columns.length >= PORTAL_TABLE_MAX_COLUMNS) return current
      const column = createPortalTableColumn(createPortalEditorBlockId, current.columns.length)
      return {
        ...current,
        columns: [...current.columns, column],
        rows: current.rows.map((row) => ({
          ...row,
          cells: { ...row.cells, [column.id]: '' },
        })),
      }
    })
  }

  const removeColumn = (columnIndex: number) => {
    const column = draft.columns[columnIndex]
    if (!column) return
    const commit = () =>
      setDraft((current) => ({
        ...current,
        columns: current.columns.filter((_, index) => index !== columnIndex),
        rows: current.rows.map((row) => ({
          ...row,
          cells: Object.fromEntries(
            Object.entries(row.cells).filter(([columnId]) => columnId !== column.id)
          ),
        })),
      }))
    const hasData = draft.rows.some((row) => row.cells[column.id]?.trim())
    if (!hasData) {
      commit()
      return
    }
    Modal.confirm({
      title: `Remover a coluna “${column.header || columnIndex + 1}”?`,
      content: 'Os dados desta coluna serão descartados.',
      okText: 'Remover',
      cancelText: 'Cancelar',
      okType: 'danger',
      onOk: commit,
    })
  }

  const addRow = () => {
    setDraft((current) =>
      current.rows.length >= PORTAL_TABLE_MAX_ROWS
        ? current
        : { ...current, rows: [...current.rows, createPortalTableRow(current.columns, createPortalEditorBlockId)] }
    )
  }

  const moveRow = (rowIndex: number, direction: -1 | 1) => {
    setDraft((current) => ({
      ...current,
      rows: moveArrayItem(current.rows, rowIndex, rowIndex + direction),
    }))
  }

  const removeRow = (rowIndex: number) => {
    const row = draft.rows[rowIndex]
    if (!row) return
    const commit = () =>
      setDraft((current) => ({
        ...current,
        rows: current.rows.filter((_, index) => index !== rowIndex),
      }))
    if (!Object.values(row.cells).some((cell) => cell.trim())) {
      commit()
      return
    }
    Modal.confirm({
      title: `Remover a linha ${rowIndex + 1}?`,
      content: 'Os dados desta linha serão descartados.',
      okText: 'Remover',
      cancelText: 'Cancelar',
      okType: 'danger',
      onOk: commit,
    })
  }

  const updateCell = (rowIndex: number, columnId: string, cell: string) => {
    setDraft((current) => ({
      ...current,
      rows: current.rows.map((row, index) =>
        index === rowIndex ? { ...row, cells: { ...row.cells, [columnId]: cell } } : row
      ),
    }))
  }

  const applyPastedGrid = (grid: PortalTableClipboardGrid) => {
    const columns = grid.headers.map((header, index) => ({
      ...createPortalTableColumn(createPortalEditorBlockId, index),
      header,
    }))
    setDraft((current) => ({
      ...current,
      columns,
      rows: grid.rows.map((cells) => ({
        id: createPortalEditorBlockId('table-row'),
        cells: Object.fromEntries(columns.map((column, index) => [column.id, cells[index] || ''])),
      })),
    }))
    setPasteOpen(false)
  }

  return (
    <>
      <Modal
        open={open}
        title="Editar tabela"
        width="calc(100vw - 48px)"
        style={{ maxWidth: 1500, top: 24 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 190px)', overflowY: 'auto' }}
        okText="Aplicar tabela"
        cancelText="Cancelar"
        onOk={() => onApply(cloneTable(draft))}
        onCancel={requestClose}
        maskClosable={false}
        keyboard={false}
      >
        <EditorStack>
          <IntroText>
            Monte a tabela manualmente ou cole uma grade pronta. O nome identifica a tabela no CMS,
            no arquivo exportado e para leitores de tela; ele não aparece como título no artigo.
          </IntroText>

          <SettingsGrid>
            <FieldGroup>
              <label htmlFor={`portal-table-name-${draft.id}`}>Nome interno da tabela</label>
              <Input
                id={`portal-table-name-${draft.id}`}
                value={draft.name}
                placeholder="Ex.: Amostra salarial por região"
                onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
              />
            </FieldGroup>
            <ExportSettings>
              <Switch
                checked={exportEnabled}
                onChange={(enabled) =>
                  setDraft((current) => ({
                    ...current,
                    export: { ...current.export, enabled },
                  }))
                }
              />
              <span>Exibir exportação</span>
            </ExportSettings>
            <FieldGroup>
              <label htmlFor={`portal-table-file-${draft.id}`}>Nome do arquivo (opcional)</label>
              <Input
                id={`portal-table-file-${draft.id}`}
                value={draft.export?.fileName || ''}
                disabled={!exportEnabled}
                placeholder="Gerado pelo nome interno"
                addonAfter=".xlsx"
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    export: { ...current.export, fileName: event.target.value },
                  }))
                }
              />
            </FieldGroup>
          </SettingsGrid>

          <SectionHeader>
            <div>
              <Typography.Title level={5}>Colunas</Typography.Title>
              <Typography.Text type="secondary">
                {draft.columns.length} de {PORTAL_TABLE_MAX_COLUMNS} colunas
              </Typography.Text>
            </div>
            <Space wrap>
              <Button icon={<SnippetsOutlined />} onClick={() => setPasteOpen(true)}>
                Colar planilha
              </Button>
              <Tooltip
                title={
                  draft.columns.length >= PORTAL_TABLE_MAX_COLUMNS
                    ? `Limite de ${PORTAL_TABLE_MAX_COLUMNS} colunas atingido`
                    : undefined
                }
              >
                <Button
                  icon={<PlusOutlined />}
                  disabled={draft.columns.length >= PORTAL_TABLE_MAX_COLUMNS}
                  onClick={addColumn}
                >
                  Adicionar coluna
                </Button>
              </Tooltip>
            </Space>
          </SectionHeader>

          {draft.columns.length > 0 ? (
            <ColumnSettingsList>
              {draft.columns.map((column, columnIndex) => (
                <ColumnSettings key={column.id}>
                  <ColumnSettingsHeader>
                    <strong>Coluna {columnIndex + 1}</strong>
                    <Space size={4}>
                      <Button
                        size="small"
                        icon={<ArrowUpOutlined />}
                        disabled={columnIndex === 0}
                        onClick={() => moveColumn(columnIndex, -1)}
                      />
                      <Button
                        size="small"
                        icon={<ArrowDownOutlined />}
                        disabled={columnIndex === draft.columns.length - 1}
                        onClick={() => moveColumn(columnIndex, 1)}
                      />
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeColumn(columnIndex)}
                      />
                    </Space>
                  </ColumnSettingsHeader>
                  <FieldGroup>
                    <label>Cabeçalho</label>
                    <Input
                      value={column.header}
                      placeholder="Cabeçalho"
                      onChange={(event) => updateColumn(columnIndex, { header: event.target.value })}
                    />
                  </FieldGroup>
                  <Select
                    value={column.style}
                    options={[...PORTAL_TABLE_COLUMN_STYLE_OPTIONS]}
                    onChange={(style) => updateColumn(columnIndex, { style })}
                  />
                  <Select
                    value={column.align}
                    options={[...PORTAL_TABLE_COLUMN_ALIGN_OPTIONS]}
                    onChange={(align) => updateColumn(columnIndex, { align })}
                  />
                  <Select
                    value={column.width}
                    options={[...PORTAL_TABLE_COLUMN_WIDTH_OPTIONS]}
                    onChange={(width) => updateColumn(columnIndex, { width })}
                  />
                </ColumnSettings>
              ))}
            </ColumnSettingsList>
          ) : (
            <EmptyGrid>Adicione uma coluna ou cole uma planilha para começar.</EmptyGrid>
          )}

          <SectionHeader>
            <div>
              <Typography.Title level={5}>Dados</Typography.Title>
              <Typography.Text type="secondary">
                {draft.rows.length} de {PORTAL_TABLE_MAX_ROWS} linhas
              </Typography.Text>
            </div>
            <Tooltip
              title={
                draft.rows.length >= PORTAL_TABLE_MAX_ROWS
                  ? `Limite de ${PORTAL_TABLE_MAX_ROWS} linhas atingido`
                  : draft.columns.length === 0
                  ? 'Adicione ao menos uma coluna primeiro'
                  : undefined
              }
            >
              <Button
                icon={<PlusOutlined />}
                disabled={draft.rows.length >= PORTAL_TABLE_MAX_ROWS || draft.columns.length === 0}
                onClick={addRow}
              >
                Adicionar linha
              </Button>
            </Tooltip>
          </SectionHeader>

          {draft.columns.length > 0 && draft.rows.length > 0 ? (
            <GridScroll>
              <DataGrid $columnCount={draft.columns.length}>
                <GridHeader aria-hidden />
                {draft.columns.map((column) => (
                  <GridColumnHeader key={column.id}>{column.header || 'Sem cabeçalho'}</GridColumnHeader>
                ))}
                <GridHeader aria-hidden />
                {draft.rows.map((row, rowIndex) => (
                  <React.Fragment key={row.id}>
                    <RowNumber>{rowIndex + 1}</RowNumber>
                    {draft.columns.map((column) => (
                      <Input.TextArea
                        key={column.id}
                        autoSize={{ minRows: 2, maxRows: 6 }}
                        value={row.cells[column.id] || ''}
                        aria-label={`Linha ${rowIndex + 1}, ${column.header || `coluna ${draft.columns.indexOf(column) + 1}`}`}
                        onChange={(event) => updateCell(rowIndex, column.id, event.target.value)}
                      />
                    ))}
                    <RowActions>
                      <Button
                        size="small"
                        icon={<ArrowUpOutlined />}
                        disabled={rowIndex === 0}
                        onClick={() => moveRow(rowIndex, -1)}
                      />
                      <Button
                        size="small"
                        icon={<ArrowDownOutlined />}
                        disabled={rowIndex === draft.rows.length - 1}
                        onClick={() => moveRow(rowIndex, 1)}
                      />
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeRow(rowIndex)}
                      />
                    </RowActions>
                  </React.Fragment>
                ))}
              </DataGrid>
            </GridScroll>
          ) : (
            <EmptyGrid>Adicione uma linha para preencher os dados da tabela.</EmptyGrid>
          )}
        </EditorStack>
      </Modal>
      <PortalTablePasteModal
        open={pasteOpen}
        hasCurrentContent={hasPortalTableGridContent(draft)}
        onCancel={() => setPasteOpen(false)}
        onApply={applyPastedGrid}
      />
    </>
  )
}

function cloneTable(table: PortalArticleTableItem) {
  return JSON.parse(JSON.stringify(table)) as PortalArticleTableItem
}

function moveArrayItem<T>(items: T[], fromIndex: number, toIndex: number) {
  if (toIndex < 0 || toIndex >= items.length) return items
  const next = [...items]
  const [item] = next.splice(fromIndex, 1)
  next.splice(toIndex, 0, item)
  return next
}

const EditorStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  color: ${portalEditorColors.navy};
`

const IntroText = styled.p`
  margin: 0;
  padding: 14px 16px;
  border-radius: 8px;
  background: #fff6db;
  color: #4b5563;
`

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 2fr) minmax(180px, auto) minmax(260px, 1fr);
  align-items: end;
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 12px;
    font-weight: 600;
  }
`

const ExportSettings = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  font-weight: 600;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  .ant-typography {
    margin: 0;
  }
`

const ColumnSettingsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 12px;
`

const ColumnSettings = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border: 1px solid ${portalEditorColors.border};
  border-radius: 8px;
  background: ${portalEditorColors.hover};
`

const ColumnSettingsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const GridScroll = styled.div`
  overflow-x: auto;
  padding-bottom: 6px;
`

const DataGrid = styled.div<{ $columnCount: number }>`
  display: grid;
  grid-template-columns: 44px repeat(${({ $columnCount }) => $columnCount}, minmax(210px, 1fr)) 116px;
  gap: 8px;
  min-width: ${({ $columnCount }) => 44 + $columnCount * 218 + 116}px;
  align-items: start;
`

const GridHeader = styled.div`
  position: sticky;
  top: 0;
  min-height: 40px;
  background: ${portalEditorColors.surface};
  z-index: 1;
`

const GridColumnHeader = styled.div`
  position: sticky;
  top: 0;
  min-height: 40px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #eaf0f6;
  font-size: 12px;
  font-weight: 700;
  z-index: 1;
`

const RowNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 54px;
  color: ${portalEditorColors.muted};
  font-weight: 700;
`

const RowActions = styled.div`
  display: flex;
  gap: 4px;
  padding-top: 7px;
`

const EmptyGrid = styled.div`
  padding: 28px;
  border: 1px dashed ${portalEditorColors.border};
  border-radius: 8px;
  color: ${portalEditorColors.muted};
  text-align: center;
  background: ${portalEditorColors.hover};
`
